import { authClient } from "@repo/db"
import { AnimatedCard, cn } from "@repo/shad"
import { Button } from "@repo/shad/ui/button"
import { CardContent, CardDescription, CardHeader, CardTitle } from "@repo/shad/ui/card"
import { Input } from "@repo/shad/ui/input"
import { Label } from "@repo/shad/ui/label"
import { AuthLayout, getFirstFormErrorMessage } from "@repo/ui"
import { useForm } from "@tanstack/react-form"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { AlertCircle, CheckCircle2 } from "lucide-react"
import { useEffect, useState } from "react"
import { z } from "zod"

export const Route = createFileRoute("/reset-password")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || undefined,
    error: (search.error as string) || undefined,
  }),
  component: ResetPassword,
})

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, "Must be at least 8 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((value) => value.password === value.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords do not match",
  })

type ResetPasswordData = z.infer<typeof resetPasswordSchema>

function ResetPassword() {
  const { token, error: urlError } = Route.useSearch()
  const navigate = useNavigate()
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(urlError ?? null)

  useEffect(() => {
    if (!token) return

    const url = new URL(window.location.href)
    url.searchParams.delete("token")
    window.history.replaceState({}, "", url.toString())
  }, [token])

  const form = useForm({
    defaultValues: {
      password: "",
      confirmPassword: "",
    } satisfies ResetPasswordData,
    validators: {
      onChange: resetPasswordSchema,
      onSubmit: resetPasswordSchema,
    },
    onSubmit: async ({ value }) => {
      if (!token) {
        setError("Missing reset token. Please request a new link.")
        return
      }

      setError(null)
      const { error: authError } = await authClient.resetPassword({
        newPassword: value.password,
        token,
      })

      if (authError) {
        setError(authError.message ?? "Failed to reset password")
        return
      }

      setIsSuccess(true)
    },
  })

  const hasToken = Boolean(token)

  return (
    <div className="dark">
      <AuthLayout>
        <AnimatedCard
          className={cn(
            "w-full",
            "card-moon shadow-moon inset-shadow-moon border-none bg-card/80 rounded-t-xl before:rounded-t-xl",
            error ? "border border-destructive" : "",
          )}
        >
          {isSuccess ? (
            <SuccessState
              onLogin={() => navigate({ to: "/login", search: { redirect: undefined } })}
            />
          ) : !hasToken ? (
            <ErrorState message={error ?? "Invalid or missing reset link."} />
          ) : (
            <>
              <CardHeader className="text-center">
                <CardTitle className="text-xl">Set new password</CardTitle>
                <CardDescription>
                  {error ? (
                    <span className="text-destructive">{error}</span>
                  ) : (
                    "Enter your new password below"
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                    void form.handleSubmit()
                  }}
                  className="grid gap-6"
                >
                  <form.Field name="password">
                    {(field) => {
                      const passwordError = getFirstFormErrorMessage(field.state.meta.errors)
                      const showPasswordError =
                        Boolean(passwordError) &&
                        (field.state.meta.isTouched || form.state.submissionAttempts > 0)

                      return (
                        <div className="grid gap-3">
                          <Label htmlFor={field.name}>New password</Label>
                          <Input
                            id={field.name}
                            name={field.name}
                            type="password"
                            autoComplete="new-password"
                            placeholder="••••••••"
                            disabled={form.state.isSubmitting}
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(event) => field.handleChange(event.target.value)}
                            aria-invalid={showPasswordError}
                          />
                          {showPasswordError ? (
                            <p className="text-sm text-destructive">{passwordError}</p>
                          ) : null}
                        </div>
                      )
                    }}
                  </form.Field>

                  <form.Field name="confirmPassword">
                    {(field) => {
                      const confirmPasswordError = getFirstFormErrorMessage(field.state.meta.errors)
                      const showConfirmPasswordError =
                        Boolean(confirmPasswordError) &&
                        (field.state.meta.isTouched || form.state.submissionAttempts > 0)

                      return (
                        <div className="grid gap-3">
                          <Label htmlFor={field.name}>Confirm password</Label>
                          <Input
                            id={field.name}
                            name={field.name}
                            type="password"
                            autoComplete="new-password"
                            placeholder="••••••••"
                            disabled={form.state.isSubmitting}
                            value={field.state.value}
                            onBlur={field.handleBlur}
                            onChange={(event) => field.handleChange(event.target.value)}
                            aria-invalid={showConfirmPasswordError}
                          />
                          {showConfirmPasswordError ? (
                            <p className="text-sm text-destructive">{confirmPasswordError}</p>
                          ) : null}
                        </div>
                      )
                    }}
                  </form.Field>

                  <Button type="submit" className="w-full" loading={form.state.isSubmitting}>
                    Reset password
                  </Button>
                  <div className="text-center text-sm">
                    <Link
                      to="/login"
                      search={{ redirect: undefined }}
                      className="font-medium text-primary hover:underline"
                    >
                      Back to sign in
                    </Link>
                  </div>
                </form>
              </CardContent>
            </>
          )}
        </AnimatedCard>
      </AuthLayout>
    </div>
  )
}

function SuccessState({ onLogin }: { onLogin: () => void }) {
  return (
    <>
      <CardHeader className="text-center">
        <CardTitle className="text-xl">Password reset</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-center space-y-4">
          <div className="mx-auto flex items-center justify-center size-12 rounded-full bg-green-100 dark:bg-green-900/30">
            <CheckCircle2 className="size-6 text-green-600 dark:text-green-400" />
          </div>
          <p className="text-sm text-muted-foreground">
            Your password has been updated. You can now sign in with your new password.
          </p>
          <Button className="w-full" onClick={onLogin}>
            Sign in
          </Button>
        </div>
      </CardContent>
    </>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <>
      <CardHeader className="text-center">
        <CardTitle className="text-xl">Reset link invalid</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-center space-y-4">
          <div className="mx-auto flex items-center justify-center size-12 rounded-full bg-destructive/10">
            <AlertCircle className="size-6 text-destructive" />
          </div>
          <p className="text-sm text-muted-foreground">{message}</p>
          <Button asChild className="w-full" variant="outline">
            <Link to="/forgot-password">Request a new link</Link>
          </Button>
          <div>
            <Link
              to="/login"
              search={{ redirect: undefined }}
              className="font-medium text-primary hover:underline text-sm"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </CardContent>
    </>
  )
}
